<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import { useMutation } from '@vue/apollo-composable';
import { useNotificationStore } from '@/stores/notification';
import { graphql } from '@/gql';
import { watchEffect } from 'vue';

const props = defineProps<{ escalationId: string }>();
const emit = defineEmits<{ success: []; loading: [value: boolean] }>();

const { t } = useI18n({
    messages: {
        nl: {
            delete: 'Verwijderen',
            delete_title: 'Escalatie verwijderen',
            delete_confirmation:
                'Weet je zeker dat je deze escalatie wilt verwijderen?',
            delete_success: 'Escalatie succesvol verwijderd',
            delete_error: 'Fout bij verwijderen van escalatie',
        },
        en: {
            delete: 'Delete',
            delete_title: 'Delete escalation',
            delete_confirmation:
                'Are you sure you want to delete this escalation?',
            delete_success: 'Escalation successfully deleted',
            delete_error: 'Error deleting escalation',
        },
    },
});

const notificationStore = useNotificationStore();

const { mutate: deleteEscalation, loading: deleteEscalationLoading } =
    useMutation(
        graphql(`
            mutation DeleteEscalation($input: DeleteEscalationInput!) {
                deleteEscalation(input: $input) {
                    ok
                }
            }
        `),
        {
            refetchQueries: ['Deliverable', 'DeliverableBottlenecks'],
            awaitRefetchQueries: true,
        }
    );

watchEffect(() => {
    emit('loading', deleteEscalationLoading.value);
});

async function handleDelete() {
    try {
        await deleteEscalation({
            input: { escalationId: props.escalationId },
        });
        emit('success');
        notificationStore.add({
            type: 'success',
            message: t('delete_success'),
        });
    } catch {
        notificationStore.add({ type: 'error', message: t('delete_error') });
    }
}
</script>

<template>
    <ConfirmationDialog
        :title="t('delete_title')"
        :text="t('delete_confirmation')"
        :confirm-button-text="t('delete')"
        destructive
        @confirm="handleDelete"
    >
        <template #activator="{ props: activatorProps }">
            <slot
                name="activator"
                :props="activatorProps"
                :loading="deleteEscalationLoading"
            />
        </template>
    </ConfirmationDialog>
</template>
